import * as R from 'ramda';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';
import {
  FiscalCalType,
  genPatternBasedCal,
  GenPatternCalArgs
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { keysToCamel } from '@blisspointmedia/bpm-types/dist/StyleUtils';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { useAppDispatch, useAppSelector } from '../../redux';
import { CompanyInfo, companyInfoSelector, SetCompanyInfo } from '../../redux/company';
import { useSetError } from '../../redux/modals';
import { awaitJSON, UsersLambdaFetch } from '../fetch-utils';
import { TestCompanyParams } from '../routing/location-constants';
import { useNavLocations } from '../routing/location-utils';
import { useLocationCompany } from './useLocationCompany';

const DEFAULT_TEST_COMPANY_PARAMS: TestCompanyParams = {
  channel: '',
  pageID: ''
};

const fetchedKeys = new Set<string>();

// Backstop against a reconcile loop: if the store never converges to the map
// value for a key (e.g. the reducer and this effect disagree on a field's
// shape), cap the number of dispatches per key so a mismatch degrades to a
// logged warning instead of an infinite render loop (React #185 / white screen).
const RECONCILE_DISPATCH_LIMIT = 5;

export const useCompanyInfoFetch: (inputCompany?: string) => void = (inputCompany?: string) => {
  const setError = useSetError();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const locationCompany = useLocationCompany();
  const cid = inputCompany ?? locationCompany ?? '';
  const companyInfo = useAppSelector(companyInfoSelector(cid));
  const { flatLocations } = useNavLocations();
  const [companyInfoMap, setCompanyInfoMap] = useState<Record<string, CompanyInfo>>({});
  const reconcileCounts = useRef<Record<string, number>>({});

  const isTestCompany = TEST_COMPANIES.includes(cid);
  const currentLocation = useMemo(() => {
    const allMatches = flatLocations.filter(route => location.pathname.includes(route.fullPath));
    if (allMatches.length === 0) {
      return undefined;
    }

    return allMatches.reduce(
      (best, route) => (route.fullPath.length > best.fullPath.length ? route : best),
      allMatches[0]
    );
  }, [flatLocations, location.pathname]);

  const testParams: TestCompanyParams = useMemo(
    () => currentLocation?.testCompanyParams ?? DEFAULT_TEST_COMPANY_PARAMS,
    [currentLocation?.testCompanyParams]
  );

  const companyInfoKey = useMemo(
    () => `${cid}_${testParams.channel}_${testParams.pageID}`,
    [cid, testParams]
  );

  // If companyInfoKey is present in map store, but does not match current-company store, update store
  useEffect(() => {
    if (!companyInfoMap[companyInfoKey] || R.equals(companyInfo, companyInfoMap[companyInfoKey])) {
      return;
    }

    const dispatchCount = reconcileCounts.current[companyInfoKey] ?? 0;
    if (dispatchCount >= RECONCILE_DISPATCH_LIMIT) {
      datadogRum.addError(
        new Error(
          `useCompanyInfoFetch: reconcile loop aborted for "${companyInfoKey}" after ` +
            `${RECONCILE_DISPATCH_LIMIT} dispatches; store never converged to map value`
        ),
        { context: 'useCompanyInfoFetch' }
      );
      return;
    }

    reconcileCounts.current[companyInfoKey] = dispatchCount + 1;
    dispatch(SetCompanyInfo(cid, companyInfoMap[companyInfoKey]));
  }, [cid, companyInfo, companyInfoKey, companyInfoMap, dispatch]);

  // If companyInfoKey is not present in map store, but we have the necessary info to try to fetch
  useEffect(() => {
    const fetch = async () => {
      fetchedKeys.add(companyInfoKey);
      try {
        const res = await UsersLambdaFetch('/company', {
          params: {
            company: cid,
            ...testParams
          }
        });
        const info = await awaitJSON<CompanyInfo>(res);

        // The API returns `null` (not `[]`) for companies with no custom dashboards, same as it
        // can for `experiments` (see the `?? []` guard on that field a few lines below in
        // location-utils.tsx) — normalize once here so every useCompanyInfo() consumer can trust
        // the declared non-null array type instead of guarding at each call site.
        if (info) {
          info.custom_dashboards = info.custom_dashboards ?? [];
        }

        if (info && isTestCompany) {
          info.fiscal_cal = {}; // standard cal
        }

        if (info && info.fiscal_cal) {
          info.fiscal_cal = keysToCamel(info.fiscal_cal);
          if (info.fiscal_cal.calType === FiscalCalType.PATTERN) {
            info.fiscal_cal.generatedPatternCal = genPatternBasedCal(
              info.fiscal_cal as GenPatternCalArgs
            );
          }
        }

        setCompanyInfoMap(current => ({ ...current, [companyInfoKey]: info }));
        dispatch(SetCompanyInfo(cid, info) as any);
      } catch (e) {
        fetchedKeys.delete(companyInfoKey);
        setError({
          message: `Failed to fetch info for company "${cid}". ${e.message}`,
          reportError: e
        });
      }
    };

    if (
      R.isNil(companyInfoMap[companyInfoKey]) &&
      !fetchedKeys.has(companyInfoKey) &&
      cid &&
      location?.pathname
    ) {
      fetch();
    }
  }, [
    cid,
    companyInfoKey,
    companyInfoMap,
    dispatch,
    isTestCompany,
    location?.pathname,
    setError,
    testParams
  ]);
};
